/**
 * Country dial code utilities for phone input.
 *
 * This is the SMS gate a visitor reaches from a QR code, so it is the one most
 * likely to be used by someone who has never seen the product, on a phone, in
 * Brazil. The number-shaping rules live in `@x9/phone-e164` and are shared with
 * the portal's sign-in so the same typed number works on both.
 */
import {
  DEFAULT_DIAL_CODE as SHARED_DEFAULT_DIAL_CODE,
  isPlausiblePhone as sharedIsPlausiblePhone,
  phoneInputProblem as sharedPhoneInputProblem,
  toE164 as sharedToE164,
} from '@x9/phone-e164';

export type { PhoneInputProblem } from '@x9/phone-e164';

export interface CountryOption {
  code: string;
  dial: string;
  name: string;
}

export const COUNTRY_OPTIONS: CountryOption[] = [
  { code: 'BR', dial: '+55', name: 'Brazil' },
  { code: 'US', dial: '+1', name: 'United States' },
  { code: 'PT', dial: '+351', name: 'Portugal' },
  { code: 'GB', dial: '+44', name: 'United Kingdom' },
  { code: 'DE', dial: '+49', name: 'Germany' },
  { code: 'FR', dial: '+33', name: 'France' },
  { code: 'ES', dial: '+34', name: 'Spain' },
  { code: 'IT', dial: '+39', name: 'Italy' },
  { code: 'AR', dial: '+54', name: 'Argentina' },
  { code: 'CO', dial: '+57', name: 'Colombia' },
  { code: 'MX', dial: '+52', name: 'Mexico' },
  { code: 'CL', dial: '+56', name: 'Chile' },
  { code: 'PE', dial: '+51', name: 'Peru' },
  { code: 'IN', dial: '+91', name: 'India' },
  { code: 'AU', dial: '+61', name: 'Australia' },
  { code: 'CA', dial: '+1', name: 'Canada' },
  { code: 'JP', dial: '+81', name: 'Japan' },
];

/** Brazil is the primary market, so an unanswered lookup pre-fills +55. */
export const DEFAULT_DIAL_CODE = SHARED_DEFAULT_DIAL_CODE;

/**
 * Resolve the visitor's country from their IP.
 *
 * ipwho.is, matching the portal. This used to ask ipapi.co, which returns
 * nothing from some networks — and because every failure path falls back to
 * +55, a wrong guess looked exactly like a correct one.
 */
export async function detectDialCode(timeoutMs = 3000): Promise<string> {
  try {
    const resp = await fetch('https://ipwho.is/', { signal: AbortSignal.timeout(timeoutMs) });
    // A rate-limit or error page can still be valid JSON; without this check
    // its shape gets read as a country answer.
    if (!resp.ok) return DEFAULT_DIAL_CODE;
    const data = await resp.json();
    // ipwho.is reports failure in the body with success:false, HTTP 200.
    if (data?.success === false) return DEFAULT_DIAL_CODE;
    const iso = typeof data?.country_code === 'string' ? data.country_code : null;
    if (!iso) return DEFAULT_DIAL_CODE;
    const match = COUNTRY_OPTIONS.find((c) => c.code === iso);
    return match?.dial ?? DEFAULT_DIAL_CODE;
  } catch {
    return DEFAULT_DIAL_CODE;
  }
}

export const toE164 = sharedToE164;

/** Kept as (dial, local) — this surface's callers pass both. */
export function isPlausiblePhone(_dial: string, local: string): boolean {
  return sharedIsPlausiblePhone(local);
}

/** Why this number will not receive an SMS, or null when it should. */
export const phoneInputProblem = sharedPhoneInputProblem;
